import React, { useState, useMemo } from 'react';
import {
  Stack,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ChipBadge } from 'new/elements/ChipBadge';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  PlayArrow as ActivateIcon,
  Pause as PauseIcon,
  Visibility as ViewToolsIcon,
  Edit as EditIcon,
  FileCopy as CloneIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { SearchRounded } from '@mui/icons-material';
import { ReactComponent as ToolCropIcon } from 'new/components/icons/fi-rr-tool-crop.svg';
import { ReactComponent as PlugIcon } from 'new/components/icons/fi-rr-plug.svg';
import { ReactComponent as PlugConnectorIcon } from 'new/components/icons/plug-connector.svg';
import { ReactComponent as InfoSvg } from 'new/components/icons/fi-rr-info.svg';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import Typography from 'new/elements/Typography';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import { ToolsetCardSkeleton, SkeletonList } from '../AIToolsSection';
import { useURLState } from 'new/hooks/useURLState';
import { ToolsetToolsDialog } from './ToolsetToolsDialog';
import { ToolsetDetailsDrawer } from './ToolsetDetailsDrawer';
import CreateToolsetDialog from './CreateToolsetDialog';
import { CloneToolsetDialog } from './CloneToolsetDialog';
import { formatRelativeTime } from '../../utils/dateFormatters';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import { ErrorState } from 'new/elements/ErrorState';
import { EmptyStatePresets } from '../../components/EmptyState';
import {
  useGetToolsetsQuery,
  useCreateToolsetMutation,
  useActivateToolsetMutation,
  usePauseToolsetMutation,
  useDeleteToolsetMutation,
  useCloneToolsetMutation,
  useSyncToolsetMutation,
} from '../../state/aiApi';
import type { ApiToolset, CreateToolsetPayload } from '../../state/aiApiTypes';
import { getStatusChipColor } from 'new/modules/ai/utils/statusColorHelpers';
import { getStatusLabel, type StatusFilter } from '../../utils/toolLabels';
import { useListFiltering } from '../../hooks/useListFiltering';
import { extractApiError } from '../../utils/errorUtils';
import { useCopyFeedback } from '../../utils/uiHelpers';
import styles from './ToolsetsTab.module.scss';

const EMPTY_TOOLSETS: ApiToolset[] = [];

// Toolset Card Component — uses ApiToolset directly
const ToolsetCard: React.FC<{
  toolset: ApiToolset;
  onViewTools: (toolset: ApiToolset) => void;
  onDelete: (toolsetId: number) => void;
  onActivate: (toolsetId: number) => void;
  onPause: (toolsetId: number) => void;
  onOpenDetails: (toolset: ApiToolset, editMode?: boolean) => void;
  onClone: (toolset: ApiToolset) => void;
  onSync: (toolset: ApiToolset) => void;
  isSyncing: boolean;
}> = ({
  toolset,
  onViewTools,
  onDelete,
  onActivate,
  onPause,
  onOpenDetails,
  onClone,
  onSync,
  isSyncing,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { copy } = useCopyFeedback();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = toolset.status === 'active';
  const hasMcpUrl = isActive && toolset.mcp_url;
  const toolsetId = toolset.tool_set_id;

  return (
    <div className={styles.toolsetCard}>
      <div className={styles.toolsetCardContent}>
        <div className={styles.toolsetCardHeader}>
          <div className={styles.toolsetIcon}>
            <PlugIcon />
          </div>

          <div className={styles.toolsetInfo}>
            <Typography
              variant="body2"
              fontWeight={500}
              className={styles.toolsetName}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(toolset);
              }}
            >
              {toolset.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {toolset.description && (
                <Typography variant="caption" color="text.secondary" className={styles.description}>
                  {toolset.description}
                </Typography>
              )}
              {toolset.updated_at && (
                <Typography variant="caption" color="text.secondary" className={styles.updatedTime}>
                  {toolset.description ? ' · ' : ''}Updated {formatRelativeTime(toolset.updated_at)}
                </Typography>
              )}
            </Stack>
          </div>

          <div className={styles.badges}>
            <div className={styles.toolCountBadge}>
              <PlugConnectorIcon className={styles.toolCountIcon} />
              <span>{toolset.tool_count ?? toolset.tools?.length ?? 0}</span>
            </div>
            {toolset.mcp_gateway_enabled && (
              <ChipBadge label="Gateway" color="secondary" variant="outlined" />
            )}
            <ChipBadge
              label={getStatusLabel(toolset.status)}
              color={getStatusChipColor(toolset.status)}
            />
          </div>

          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(toolset);
              }}
              className={styles.infoButton}
            >
              <InfoSvg style={{ width: 18, height: 18 }} />
            </IconButton>
          </Tooltip>

          <div className={styles.toolsetActions} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={handleMenuOpen} className={styles.actionButton}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem
                onClick={() => {
                  onViewTools(toolset);
                  handleMenuClose();
                }}
              >
                <ViewToolsIcon fontSize="small" className={styles.menuIcon} />
                Manage Tools
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onOpenDetails(toolset, true);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" className={styles.menuIcon} />
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onClone(toolset);
                  handleMenuClose();
                }}
              >
                <CloneIcon fontSize="small" className={styles.menuIcon} />
                Clone
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onSync(toolset);
                  handleMenuClose();
                }}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <CircularProgress size={16} className={styles.menuIcon} />
                ) : (
                  <SyncIcon fontSize="small" className={styles.menuIcon} />
                )}
                Sync All Tools
              </MenuItem>
              <Divider />
              {!isActive && (
                <MenuItem
                  onClick={() => {
                    onActivate(toolsetId);
                    handleMenuClose();
                  }}
                >
                  <ActivateIcon fontSize="small" className={styles.menuIcon} />
                  Activate
                </MenuItem>
              )}
              {isActive && (
                <MenuItem
                  onClick={() => {
                    onPause(toolsetId);
                    handleMenuClose();
                  }}
                >
                  <PauseIcon fontSize="small" className={styles.menuIcon} />
                  Pause
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  copy(String(toolsetId), 'toolset-id');
                  handleMenuClose();
                }}
              >
                <CopyIcon fontSize="small" className={styles.menuIcon} />
                Copy ID
              </MenuItem>
              {hasMcpUrl && (
                <MenuItem
                  onClick={() => {
                    copy(toolset.mcp_url!, 'mcp-url');
                    handleMenuClose();
                  }}
                >
                  <CopyIcon fontSize="small" className={styles.menuIcon} />
                  Copy MCP URL
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  onDelete(toolsetId);
                  handleMenuClose();
                }}
                className={styles.deleteMenuItem}
              >
                <DeleteIcon fontSize="small" className={styles.menuIcon} />
                Delete
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToolsetsTab: React.FC = () => {
  const { getParam, setParam, setParams } = useURLState();
  const { showSuccess, showError } = useSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  // URL state for dialogs
  const createDialogOpen = getParam('modal') === 'create-toolset';
  const viewToolsDialogOpen = getParam('modal') === 'view-toolset-tools';
  const selectedToolsetId = getParam('toolsetId');

  // Details drawer state
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [detailsDrawerEditMode, setDetailsDrawerEditMode] = useState(false);
  const [selectedToolsetForDetails, setSelectedToolsetForDetails] = useState<ApiToolset | null>(
    null,
  );

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [toolsetToClone, setToolsetToClone] = useState<ApiToolset | null>(null);

  // RTK Query — single source of truth
  const {
    data: toolsets = EMPTY_TOOLSETS,
    isLoading,
    error,
    refetch,
  } = useGetToolsetsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const [createToolset, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateToolsetMutation();
  const [activateToolset] = useActivateToolsetMutation();
  const [pauseToolset] = usePauseToolsetMutation();
  const [deleteToolset] = useDeleteToolsetMutation();
  const [cloneToolset, { isLoading: isCloning }] = useCloneToolsetMutation();
  const [syncToolset] = useSyncToolsetMutation();
  const [syncingToolsetIds, setSyncingToolsetIds] = useState<Set<number>>(new Set());

  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    paginatedItems: paginatedToolsets,
    totalItems,
    totalPages,
    safePage,
    handlePageChange,
    statusCounts,
    clearFilters,
  } = useListFiltering({
    items: toolsets,
    searchFields: (ts) => [ts.name, ts.description],
  });

  // Find toolset for View Tools dialog
  const selectedToolsetForTools = useMemo(() => {
    if (!selectedToolsetId) return null;
    return toolsets.find((t) => String(t.tool_set_id) === selectedToolsetId) || null;
  }, [selectedToolsetId, toolsets]);

  const handleViewTools = (toolset: ApiToolset) => {
    setParams({
      modal: 'view-toolset-tools',
      toolsetId: String(toolset.tool_set_id),
    });
  };

  const handleDeleteToolset = (toolsetId: number) => {
    const toolset = toolsets.find((ts) => ts.tool_set_id === toolsetId);
    confirm({
      title: 'Delete Toolset',
      message: `Are you sure you want to delete "${toolset?.name || 'this toolset'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteToolset(toolsetId).unwrap();
          showSuccess('Toolset deleted successfully');
          refetch();
        } catch (err) {
          showError(extractApiError(err, 'Failed to delete toolset'));
        }
      },
    });
  };

  const handleActivateToolset = async (toolsetId: number) => {
    try {
      await activateToolset(toolsetId).unwrap();
      showSuccess('Toolset activated successfully');
    } catch (err) {
      showError(extractApiError(err, 'Failed to activate toolset'));
    }
  };

  const handlePauseToolset = async (toolsetId: number) => {
    try {
      await pauseToolset(toolsetId).unwrap();
      showSuccess('Toolset paused successfully');
    } catch (err) {
      showError(extractApiError(err, 'Failed to pause toolset'));
    }
  };

  const handleCreateToolset = async (data: CreateToolsetPayload) => {
    try {
      await createToolset(data).unwrap();
      setParam('modal', null);
      showSuccess('Toolset created successfully');
    } catch (err) {
      throw err;
    }
  };

  const handleOpenDetails = (toolset: ApiToolset, editMode?: boolean) => {
    setSelectedToolsetForDetails(toolset);
    setDetailsDrawerEditMode(editMode || false);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    setDetailsDrawerEditMode(false);
    setSelectedToolsetForDetails(null);
  };

  const handleClone = (toolset: ApiToolset) => {
    setToolsetToClone(toolset);
    setCloneDialogOpen(true);
  };

  const handleCloneSubmit = async (data: { name: string; description?: string }) => {
    if (!toolsetToClone) return;
    try {
      await cloneToolset({ id: toolsetToClone.tool_set_id, data }).unwrap();
      setCloneDialogOpen(false);
      setToolsetToClone(null);
      showSuccess('Toolset cloned successfully');
    } catch (err) {
      throw err;
    }
  };

  const handleSyncToolset = async (toolset: ApiToolset) => {
    setSyncingToolsetIds((prev) => new Set(prev).add(toolset.tool_set_id));
    try {
      const result = await syncToolset(toolset.tool_set_id).unwrap();
      if (result.updated_versions > 0) {
        showSuccess(
          `Toolset "${toolset.name}" synced — ${result.updated_versions} tool(s) updated`,
        );
      } else if (result.errors === 0) {
        showSuccess(`Toolset "${toolset.name}" synced — all tools up to date`);
      }
      if (result.errors > 0) {
        showError(`${result.errors} tool(s) failed to sync`);
      }
    } catch (err) {
      showError(extractApiError(err, `Failed to sync toolset "${toolset.name}"`));
    } finally {
      setSyncingToolsetIds((prev) => {
        const next = new Set(prev);
        next.delete(toolset.tool_set_id);
        return next;
      });
    }
  };

  const errorMessage = error ? extractApiError(error, 'Failed to load toolsets') : null;

  return (
    <Stack className={styles.container}>
      {/* Controls Bar */}
      <Stack direction="row" className={styles.controlsContainer}>
        <Stack direction="row" gap="16px" alignItems="center" flex={1}>
          <TextField
            placeholder="Search toolsets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            startAdornment={<SearchRounded className={styles.searchIcon} />}
            className={styles.searchField}
          />
        </Stack>
        <Stack direction="row" gap="16px" alignItems="center">
          <CardsListPageNumbers
            isLoading={isLoading}
            currentPage={safePage}
            totalPages={totalPages}
            totalItems={totalItems}
            onListingChange={handlePageChange}
            disableHistoryUpdate
          />
          <Button
            variant="contained"
            color="primary"
            className={styles.addToolsetButton}
            startIcon={<AddIcon />}
            onClick={() => setParam('modal', 'create-toolset')}
          >
            Create Toolset
          </Button>
        </Stack>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <div className={styles.listContainer}>
          <Stack spacing={1.5} className={styles.content}>
            <SkeletonList count={5} component={ToolsetCardSkeleton} />
          </Stack>
        </div>
      ) : errorMessage ? (
        <ErrorState
          message="Failed to load toolsets"
          description={errorMessage}
          onRetry={refetch}
          isRetrying={isLoading}
        />
      ) : totalItems === 0 ? (
        searchQuery ? (
          <EmptyStatePresets.NoSearchResults
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
          />
        ) : (
          <EmptyStatePresets.NoToolsets
            onCreateToolset={() => setParam('modal', 'create-toolset')}
          />
        )
      ) : (
        <div className={styles.listContainer}>
          <Stack className={styles.content}>
            {paginatedToolsets.map((toolset) => (
              <ToolsetCard
                key={toolset.tool_set_id}
                toolset={toolset}
                onViewTools={handleViewTools}
                onDelete={handleDeleteToolset}
                onActivate={handleActivateToolset}
                onPause={handlePauseToolset}
                onOpenDetails={handleOpenDetails}
                onClone={handleClone}
                onSync={handleSyncToolset}
                isSyncing={syncingToolsetIds.has(toolset.tool_set_id)}
              />
            ))}
          </Stack>
        </div>
      )}

      {/* Create Toolset Dialog */}
      <CreateToolsetDialog
        open={createDialogOpen}
        onClose={() => {
          setParam('modal', null);
          resetCreate();
        }}
        onSubmit={handleCreateToolset}
        isLoading={isCreating}
        error={createError ? extractApiError(createError, 'Failed to create toolset') : null}
      />

      {/* Clone Toolset Dialog */}
      <CloneToolsetDialog
        open={cloneDialogOpen}
        onClose={() => {
          setCloneDialogOpen(false);
          setToolsetToClone(null);
        }}
        onSubmit={handleCloneSubmit}
        isLoading={isCloning}
        originalName={toolsetToClone?.name}
        originalDescription={toolsetToClone?.description}
      />

      {/* View Tools Dialog */}
      <ToolsetToolsDialog
        open={viewToolsDialogOpen}
        onClose={() => {
          setParams({ modal: null, toolsetId: null });
        }}
        toolsetId={selectedToolsetForTools?.tool_set_id ?? null}
        toolsetName={selectedToolsetForTools?.name}
        toolsetDescription={selectedToolsetForTools?.description}
      />

      {ConfirmDialogComponent}

      {/* Details Drawer */}
      <ToolsetDetailsDrawer
        toolsetId={selectedToolsetForDetails?.tool_set_id ?? null}
        open={detailsDrawerOpen}
        onClose={handleCloseDetails}
        initialEditMode={detailsDrawerEditMode}
      />
    </Stack>
  );
};
